import happyLineIcon from '@assets/images/happy_line.svg'
import atLineIcon from '@assets/images/at_line.svg'
import variableIcon from '@assets/images/x-variable.svg'
import signatureLineIcon from '@assets/images/signature_line.svg'

export const AI_ICON_COLOR = '#6c757d'

export const hexToFilter = (hex: string): string => {
  const color = hex.replace('#', '')
  const r = parseInt(color.substr(0, 2), 16)
  const g = parseInt(color.substr(2, 2), 16)
  const b = parseInt(color.substr(4, 2), 16)

  const rNorm = r / 255
  const gNorm = g / 255
  const bNorm = b / 255

  const max = Math.max(rNorm, gNorm, bNorm)
  const min = Math.min(rNorm, gNorm, bNorm)
  const diff = max - min

  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (diff !== 0) {
    s = l > 0.5 ? diff / (2 - max - min) : diff / (max + min)

    switch (max) {
      case rNorm:
        h = (gNorm - bNorm) / diff + (gNorm < bNorm ? 6 : 0)
        break
      case gNorm:
        h = (bNorm - rNorm) / diff + 2
        break
      case bNorm:
        h = (rNorm - gNorm) / diff + 4
        break
    }
    h /= 6
  }

  const hue = Math.round(h * 360)
  const sat = Math.round(s * 100)
  const light = Math.round(l * 100)

  return `brightness(0) saturate(100%) invert(${100 - light}%) sepia(${sat}%) saturate(1000%) hue-rotate(${hue}deg) brightness(${light > 50 ? 110 : 90}%) contrast(${light > 50 ? 120 : 80}%)`
}

export const AI_TOOLBAR_BUTTONS = [
  {
    key: 'happy',
    text: 'Emoji picker',
    icon: happyLineIcon,
  },
  {
    key: 'mention',
    text: '@ Mention',
    icon: atLineIcon,
  },
  {
    key: 'variable',
    text: 'Insert Variable',
    icon: variableIcon,
  },
  {
    key: 'signature',
    text: 'Signature',
    icon: signatureLineIcon,
  },
  {
    key: 'template',
    text: 'Template',
  },
]

export const generateAIToolbarStyles = (
  iconColor: string = AI_ICON_COLOR,
  isAIModeActive: boolean = false,
): string => `
  <style>
    .ai-toolbar-icon {
      filter: ${hexToFilter(iconColor)};
      transition: filter 0.2s ease;
    }
    .ai-toolbar-icon:hover {
      filter: ${hexToFilter(iconColor)};
      opacity: 0.8;
    }
    
    .quick-note-input-wrapper {
      position: relative;
      display: flex;
      align-items: center;
      width: 100%;
    }
        
    .quick-note-overlay {
      position: absolute;
      top: 50%;
      left: 16px;
      transform: translateY(-50%);
      display: flex;
      align-items: center;
      gap: 0px;
      pointer-events: none;
      color: var(--blue-400);
      font-size: 14px;
      opacity: 1;
      transition: opacity 0.2s ease;
      z-index: 2;
    }
    
    .quick-note-overlay i {
      width: 16px;
      height: 16px;
    }
    
    .quick-note-input:focus + .quick-note-overlay {
      opacity: 0;
    }
    
    .quick-note-ai-overlay {
      position: absolute;
      top: 50%;
      left: 0px;
      transform: translateY(-50%);
      pointer-events: auto;
      z-index: 2;
      cursor: text;
      border-radius: 12px;
    }
    
    .quick-note-input:focus {
      padding-left: 16px;
    }
    
    .quick-note-input.ai-mode:focus {
      padding-left: 16px;
    }
    
    .quick-note-input.ai-mode.typing:focus {
      padding-left: 70px;
    }
    
    /* Ensure cursor is visible in AI mode with content */
    .quick-note-input.ai-mode.has-content {
      caret-color: #007bff;
    }
    
    .quick-note-input.ai-mode.has-content:focus {
      caret-color: #007bff;
    }
    
    .ai-overlay-container {
      display: flex;
      padding: 4px;
      justify-content: center;
      align-items: center;
      gap: 8px;
      border-radius: 8px;
      pointer-events: none;
    }
    
    .ai-icons-container {
      display: flex;
      padding: 4px;
      justify-content: center;
      align-items: center;
      gap: 4px;
      border-radius: 6px;
      background: var(--indigo-300);
      pointer-events: auto;
    }
    
    .ai-icon-bg {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      background: white;
      border-radius: 4px;
      pointer-events: auto;
      position: relative;
    }
    
    .ai-icon-bg i {
      width: 14px;
      height: 14px;
      color: var(--indigo-300);
      position: absolute;
      top: 50%;
      left: 1px;
      transform: translate(-50%, -50%);
      line-height: 1;
    }
    
    .ai-icons-container i.mgc_close_line {
      width: 16px;
      height: 16px;
      color: white;
      pointer-events: auto;
      cursor: pointer;
    }    
    
    .ai-text {
      color: #8A8E97;
      font-size: 14px;
      font-weight: 500;
      padding-left: 8px;
    }
    
    /* Tooltip styling for Syncfusion RichTextEditor */
    .e-tooltip-wrap {
      background: #333 !important;
      color: white !important;
      border: 1px solid #555 !important;
      border-radius: 4px !important;
      padding: 4px 8px !important;
      font-size: 12px !important;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3) !important;
    }
    
    .e-tooltip-wrap .e-tip-content {
      color: white !important;
    }
    
    .e-tooltip-wrap .e-arrow-tip {
      border-top-color: #333 !important;
    }
    
    .e-tooltip-wrap .e-arrow-tip-inner {
      border-top-color: #333 !important;
    }
    
    /* Signature Modal Styles */
    .signature-modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 9999;
    }
    
    .signature-modal {
      background: white;
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      width: 90%;
      max-width: 600px;
      max-height: 80vh;
      display: flex;
      flex-direction: column;
    }
    
    .signature-modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 20px;
      border-bottom: 1px solid #e9ecef;
    }
    
    .signature-modal-header h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: #333;
    }
    
    .signature-close-btn {
      background: none;
      border: none;
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      color: #666;
      font-size: 16px;
    }
    
    .signature-close-btn:hover {
      background: #f8f9fa;
      color: #333;
    }
    
    .signature-modal-body {
      padding: 20px;
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 300px;
    }
    
    .signature-modal-body .e-signature {
      border: 2px dashed #ddd;
      border-radius: 8px;
      width: 100%;
      height: 250px;
    }
    
    .signature-modal-footer {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      padding: 16px 20px;
      border-top: 1px solid #e9ecef;
    }
    
    /* Draggable signature styles */
    .e-rte-content img[alt="Signature"] {
      cursor: move !important;
      border: 2px dashed transparent;
      transition: border-color 0.2s ease;
    }
    
    .e-rte-content img[alt="Signature"]:hover {
      border-color: #007bff;
    }
    
    .e-rte-content img[alt="Signature"]:active {
      border-color: #0056b3;
      opacity: 0.8;
    }
  </style>
`

export const generateAIToolbarButtons = (
  onButtonClick: (key: string) => void,
): Array<{
  id: string
  template: string
  undo: boolean
  tooltipText: string
  click: () => void
}> => {
  return AI_TOOLBAR_BUTTONS.map((button) => ({
    id: button.key,
    template:
      button.key === 'template'
        ? `<button class="e-tbar-btn e-btn custom-toolbar-button ${button.key}" tabindex="-1">
      <div class="template-toolbar-button">
        <span class="template-toolbar-label">${button.text}</span>
        <i class="mgc mgc_down_small_fill"></i>
      </div>
    </button>`
        : `<button class="e-tbar-btn e-btn custom-toolbar-button ${button.key}" tabindex="-1">
      <div class="e-tbar-btn-text" style="font-weight: 500">
        <img src="${button.icon}" width="16" height="16" alt="${button.text}" class="ai-toolbar-icon" />
      </div>
    </button>`,
    undo: true,
    tooltipText: button.text,
    click: () => onButtonClick(button.key),
  }))
}
